import { useEffect, useState } from 'react';
import { FiSearch, FiX, FiChevronDown } from 'react-icons/fi';
import { Link, useLocation } from 'react-router';
import {
    NAV_LINKS,
    MEGA_MENUS,
    SITE_LINKS,
    type MenuKey,
    isCurrentNavLink,
    isCurrentMenuItem,
} from '@/data/navigation';
import { Img } from '@/components/Img';
import { LEGAL } from './Footer';

/** Nigerian flag chip (all supported languages are Nigerian). */
export function NgFlag({ className = '' }: { className?: string }) {
    return (
        <span className={`inline-flex h-4 w-6 overflow-hidden rounded-[2px] ${className}`} aria-hidden>
            <span className="h-full w-1/3 bg-[#008751]" />
            <span className="h-full w-1/3 bg-white" />
            <span className="h-full w-1/3 bg-[#008751]" />
        </span>
    );
}

interface MobileMenuProps {
    open: boolean;
    onClose: () => void;
}

export default function MobileMenu({ open, onClose }: MobileMenuProps) {
    const [expanded, setExpanded] = useState<MenuKey | null>(null);
    const { pathname, hash } = useLocation();
    // Reopening the drawer should not resume the section expanded last time.
    useEffect(() => {
        if (!open) setExpanded(null);
    }, [open]);

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 z-[2050] bg-black/50 transition-opacity duration-300 lg:hidden ${
                    open ? 'opacity-100' : 'pointer-events-none opacity-0'
                }`}
                onClick={onClose}
            />
            {/* Drawer */}
            <aside
                className={`fixed inset-y-0 right-0 z-[2060] flex w-full max-w-[360px] flex-col bg-[#121212] transition-transform duration-300 lg:hidden ${
                    open ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
                {/* Top bar */}
                <div className="flex h-[68px] shrink-0 items-center justify-between border-b border-[rgba(204,204,204,0.11)] px-5">
                    <Img src="/landing/icons/logo.svg" alt="Citizens' Gavel" className="h-[26px] w-[91px]" />
                    <button type="button" onClick={onClose} aria-label="Close menu" className="p-1 text-white">
                        <FiX className="h-6 w-6" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 pt-6">
                    {/* Search */}
                    <div className="flex items-center gap-2 rounded-lg bg-[rgba(51,51,51,0.5)] p-2.5">
                        <FiSearch className="h-4 w-4 text-white/60" />
                        <input
                            type="text"
                            placeholder="Search"
                            className="w-full bg-transparent text-[12px] font-medium text-white placeholder:text-white/60 focus:outline-none"
                        />
                        <span className="flex items-center gap-1 pl-1">
                            <NgFlag />
                            <FiChevronDown className="h-4 w-4 text-white/70" />
                        </span>
                    </div>

                    {/* Nav */}
                    <nav className="mt-8 flex flex-col gap-6">
                        {NAV_LINKS.map((link) => {
                            const current = isCurrentNavLink(link, pathname);
                            if (!link.menu) {
                                return (
                                    <Link
                                        key={link.label}
                                        to={link.href ?? '/'}
                                        aria-current={current ? 'page' : undefined}
                                        className={`text-[15px] leading-6 transition-colors ${
                                            current ? 'font-bold text-white' : 'font-medium text-white/70'
                                        }`}
                                        onClick={onClose}
                                    >
                                        {link.label}
                                    </Link>
                                );
                            }
                            const menu = MEGA_MENUS[link.menu];
                            const isOpen = expanded === link.menu;
                            return (
                                <div key={link.label} className="flex flex-col">
                                    <button
                                        type="button"
                                        onClick={() => setExpanded(isOpen ? null : link.menu!)}
                                        className="flex items-center gap-2 text-left"
                                    >
                                        <span
                                            className={`text-[15px] leading-6 transition-colors ${
                                                isOpen
                                                    ? 'font-medium text-primary'
                                                    : current
                                                      ? 'font-bold text-white'
                                                      : 'font-medium text-white/70'
                                            }`}
                                        >
                                            {menu.label}
                                        </span>
                                        <FiChevronDown
                                            className={`h-5 w-5 text-primary transition-transform ${
                                                isOpen ? 'rotate-180' : ''
                                            }`}
                                        />
                                    </button>
                                    {isOpen && (
                                        <div className="mt-4 flex flex-col gap-4 pl-1">
                                            {menu.items.map((item) => {
                                                const here = isCurrentMenuItem(item.href, pathname, hash);
                                                return (
                                                    <Link
                                                        key={item.label}
                                                        to={item.href}
                                                        onClick={onClose}
                                                        aria-current={here ? 'page' : undefined}
                                                        className="flex items-center gap-3"
                                                    >
                                                        <Img
                                                            src={item.icon}
                                                            alt=""
                                                            className="h-5 w-5 object-contain"
                                                        />
                                                        <span
                                                            className={`text-[14px] ${here ? 'font-bold text-white' : 'text-white/70'}`}
                                                        >
                                                            {item.label}
                                                        </span>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </nav>

                    {SITE_LINKS.donate && (
                        <a
                            href={SITE_LINKS.donate}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={onClose}
                            className="mt-8 flex w-full items-center justify-center rounded-[10px] bg-primary px-5 py-3 text-[15px] font-bold text-white"
                        >
                            Donate
                        </a>
                    )}
                </div>

                {/* Footer */}
                <div className="shrink-0 border-t border-[rgba(204,204,204,0.11)] px-5 py-4">
                    <p className="text-[12px] leading-[1.5] text-white">© 2026 Citizen’s Gavel. All Rights Reserved.</p>
                    <div className="mt-1 flex gap-6">
                        {LEGAL.map(({ label, to }) => (
                            <Link key={label} to={to} className="text-[12px] text-primary">
                                {label}
                            </Link>
                        ))}
                    </div>
                </div>
            </aside>
        </>
    );
}
